import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { AXIOS_GET } from "../../server/api/crud";
import {
  getKlacht,
  getUser,
  getUserdetails,
  getVooruitgang,
} from "../../Localstorage/localstorage";
import RPProgressbarVragenlijst from "../../Componenten/Progressbar/RPProgressbarVragenlijst";
import { vooruitgangTabs } from "../../function";
import moment from "moment";
import VooruitgangChart from "../../Componenten/Chartjs/VooruitgangChart";
import { Attachments } from "../../Componenten/Documenten/Attachments";
import {
  ArrowRightIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  IdentificationIcon,
  InboxIcon,
  LifebuoyIcon,
  MapPinIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";

const calculateAverage = (data) => {
  const total = data?.reduce((sum, item) => sum + item.score, 0) || 0;
  return data?.length ? parseFloat((total / data.length).toFixed(2)) : 0;
};

const countCompletedValues = (object) =>
  Object.values(object || {}).filter((value) => value != null && value !== "").length;

const formatCount = (value, total) => `${value || 0}/${total || 0}`;

const getFirstName = (userdetails) =>
  userdetails?.first_name || String(userdetails?.full_name || "daar").split(" ")[0];

const getDateValue = (item, keys = ["datetime", "date", "datum", "created_at", "updated_at"]) => {
  for (const key of keys) {
    if (item?.[key]) {
      const date = moment(item[key]);
      if (date.isValid()) return date.valueOf();
    }
  }
  return 0;
};

const latestByDate = (items, keys) =>
  [...(Array.isArray(items) ? items : [])].sort((a, b) => getDateValue(b, keys) - getDateValue(a, keys))[0] || null;

const metricLabels = {
  slaap: "Slaapkwaliteit",
  energie: "Energie",
  stress: "Stress",
  cravings: "Cravings",
};

const sentStatus = (value) => Number(value) === 1 || value === true || value === "1";

const DashboardClient = ({
  overview = null,
  initialAttachments = [],
  initialSupportTickets = [],
}) => {
  const [klachten, setklachten] = useState(null);
  const [preintake, setPreintake] = useState(null);
  const [clientOverview, setClientOverview] = useState(overview);
  const [activeTab, setActiveTab] = useState("overzicht");
  const [averageScore, setAverageScore] = useState({
    slaap: 0,
    energie: 0,
    stress: 0,
    cravings: 0,
  });
  const [voedingsdagboek, setVoedingsdagboek] = useState(null);
  const [attachments, setAttachments] = useState(initialAttachments);
  const [supportTickets, setSupportTickets] = useState(initialSupportTickets);
  const [unreadMailCount, setUnreadMailCount] = useState(Number(overview?.unread_messages_count || 0));

  const user = getUser();
  const userdetails = getUserdetails();
  const vooruitgang = getVooruitgang();
  const tabs = vooruitgangTabs(user, vooruitgang);

  const [metricData, setMetricData] = useState({
    slaap: [],
    energie: [],
    stress: [],
    cravings: [],
  });

  const [selectedMetric, setSelectedMetric] = useState(tabs[0]);

  const fetchDataKlacht = useCallback(async (userId) => {
    try {
      const response = await AXIOS_GET(`klachtenregistratie/all/${userId}`);
      const dataKlachten = response?.data || {};
      const totalValue = Object.values(dataKlachten).filter(
        (value) => Array.isArray(value) && value.length > 0
      ).length;

      setklachten({
        title: "Klachtenregistratie formulier",
        subtitle: `U heeft al ${totalValue}/18 van de vragenlijst ingevuld`,
        lijst: dataKlachten,
      });
    } catch (error) {
      console.error("Error fetching data (klacht):", error);
      throw error;
    }
  }, []);

  const fetchDataPreintake = useCallback(async (userId) => {
    try {
      const response = await AXIOS_GET(`preintake/${userId}`);
      if (response?.data?.data?.[0]) setPreintake(response.data.data[0]);
    } catch (error) {
      console.error("Error fetching data (preintake):", error);
      throw error;
    }
  }, []);

  const fetchDataVoedingsdagboek = useCallback(async (userId) => {
    try {
      const response = await AXIOS_GET(`voedsel/${userId}`);
      if (response?.data?.data?.[0]) setVoedingsdagboek(response.data.data[0]);
    } catch (error) {
      console.error("Error fetching data (voedingsdagboek):", error);
      throw error;
    }
  }, []);

  const fetchVooruitgangData = useCallback(
    async (userId) => {
      try {
        const response = await AXIOS_GET(`vooruitgang/uservooruitgang/${userId}`);

        if (!response) {
          return;
        }

        if (response.status !== 201) {
          console.warn("Unexpected response status:", response?.status);
          return;
        }

        const {
          slaapDatas = [],
          energieDatas = [],
          stressDatas = [],
          cravingsDatas = [],
        } = response.data;

        const formatData = (data) =>
          data?.map((item) => ({
            ...item,
            date: moment(item.date).format("YYYY-MM-DD"),
          })) || [];

        setAverageScore({
          slaap: calculateAverage(slaapDatas),
          energie: calculateAverage(energieDatas),
          stress: calculateAverage(stressDatas),
          cravings: calculateAverage(cravingsDatas),
        });

        setMetricData({
          slaap: formatData(slaapDatas),
          energie: formatData(energieDatas),
          stress: formatData(stressDatas),
          cravings: formatData(cravingsDatas),
        });
        setSelectedMetric("slaap");
      } catch (error) {
        console.error("Error fetching data (vooruitgang):", error);
      }
    },
    [tabs]
  );

  const getDocumentByID = useCallback(async (p_user_id) => {
    const res = await AXIOS_GET(`documents/user/${p_user_id}`);
    setAttachments(res?.data?.data || []);
  }, []);

  const fetchClientOverview = useCallback(async (userId) => {
    const response = await AXIOS_GET(`users/dashboard/${userId}`);
    const row = response?.data?.data?.[0];
    if (row) setClientOverview(row);
  }, []);

  const fetchSupportDashboard = useCallback(async () => {
    const response = await AXIOS_GET("support/tickets");
    setSupportTickets(response?.data?.data || []);
  }, []);

  const fetchMailDashboard = useCallback(async () => {
    const response = await AXIOS_GET("mailbox/mailmessage/unread-count/total");
    setUnreadMailCount(Number(response?.data?.data?.unread_count || 0));
  }, []);

  useLayoutEffect(() => {
    const klachid = getKlacht();

    if (user) {
      const { id } = user;
      Promise.allSettled([
        fetchClientOverview(id),
        fetchDataKlacht(klachid?.id),
        fetchDataPreintake(id),
        fetchDataVoedingsdagboek(id),
        fetchVooruitgangData(id),
        getDocumentByID(id),
        fetchSupportDashboard(),
        fetchMailDashboard(),
      ]).then((results) => {
        results.forEach((result) => {
          if (result.status === "rejected") {
            console.error("Error fetching dashboard data:", result.reason);
          }
        });
      });
    }
  }, []);

  const dashboardStats = useMemo(() => {
    const preintakeTotal = Object.keys(preintake || {}).length;
    const preintakeDone = countCompletedValues(preintake);
    const klachtTotal = Object.keys(klachten?.lijst || {}).length;
    const klachtDone = Object.values(klachten?.lijst || {}).filter(
      (value) => Array.isArray(value) && value.length > 0
    ).length;
    const voedingDone =
      typeof voedingsdagboek?.status === "number"
        ? Math.min(voedingsdagboek.status, 3)
        : 0;
    const totalSteps = preintakeTotal + klachtTotal + 3;
    const doneSteps = preintakeDone + klachtDone + voedingDone;

    return {
      preintakeDone,
      preintakeTotal,
      klachtDone,
      klachtTotal,
      voedingDone,
      totalSteps,
      doneSteps,
      journeyPercent: totalSteps ? Math.round((doneSteps / totalSteps) * 100) : 0,
    };
  }, [klachten, preintake, voedingsdagboek]);

  const activeSupportTickets = useMemo(
    () =>
      supportTickets.filter(
        (ticket) => !["resolved", "closed"].includes(String(ticket?.status || "").toLowerCase())
      ),
    [supportTickets]
  );

  const latestDocument = useMemo(
    () => latestByDate(attachments, ["datetime", "datum", "created_at", "updated_at"]),
    [attachments]
  );

  const nextAppointment = useMemo(() => {
    if (!clientOverview?.appointment_id && !clientOverview?.appointment_title) return null;
    return {
      title: clientOverview?.appointment_title || clientOverview?.appointment_details || "Afspraak",
      date: clientOverview?.appointment_datetime || clientOverview?.appointment_date,
      time: clientOverview?.appointment_start_time,
      location: clientOverview?.appointment_location || "Locatie volgt",
      accepted: Number(clientOverview?.appointment_admin_accept) === 1,
    };
  }, [clientOverview]);

  const progressInsights = useMemo(() => {
    const rows = Object.keys(metricLabels)
      .map((key) => {
        const points = metricData?.[key] || [];
        const lastPoint = latestByDate(points, ["date"]);
        return {
          key,
          label: metricLabels[key],
          score: Number(averageScore?.[key] || 0),
          points: points.length,
          lastDate: lastPoint?.date,
        };
      })
      .filter((item) => item.points > 0 || item.score > 0);

    const sorted = [...rows].sort((a, b) => b.score - a.score);
    return {
      strongest: sorted[0] || null,
      attention: sorted[sorted.length - 1] || null,
      totalPoints: rows.reduce((sum, item) => sum + item.points, 0),
      latestUpdate: latestByDate(
        rows.map((item) => ({ date: item.lastDate })).filter((item) => item.date),
        ["date"]
      )?.date,
    };
  }, [averageScore, metricData]);

  const sentForms = useMemo(
    () => [
      {
        label: "Pre-intake",
        sent: sentStatus(clientOverview?.vragenlijsten_preintake_verstuur),
        date: clientOverview?.verstuur_preintake_date,
      },
      {
        label: "Klachten",
        sent: sentStatus(clientOverview?.vragenlijsten_klacht_verstuur),
        date: clientOverview?.verstuur_klacht_date,
      },
      {
        label: "Voeding",
        sent: sentStatus(clientOverview?.vragenlijsten_voeding_verstuur),
        date: clientOverview?.verstuur_voeding_date,
      },
    ],
    [clientOverview]
  );

  const nextAction = useMemo(() => {
    if (!preintake) {
      return {
        title: "Start met uw pre-intake",
        text: "Vul eerst uw basisgegevens in zodat uw specialist het traject goed kan voorbereiden.",
        href: "/healthforms",
        label: "Open vragenlijsten",
      };
    }

    if (
      dashboardStats.klachtTotal > 0 &&
      dashboardStats.klachtDone < dashboardStats.klachtTotal
    ) {
      return {
        title: "Werk uw klachtenregistratie bij",
        text: "Er ontbreken nog categorieen. Een volledig beeld maakt het advies persoonlijker.",
        href: "/healthforms",
        label: "Ga verder",
      };
    }

    if (dashboardStats.voedingDone < 3) {
      return {
        title: "Vul uw voedingsdagboek aan",
        text: "Nog een klein onderdeel nodig voor een duidelijker weekbeeld.",
        href: "/eetdagboek",
        label: "Open dagboek",
      };
    }

    return {
      title: "Bekijk uw vooruitgang",
      text: "Uw basis is compleet. Volg nu hoe slaap, energie, stress en cravings evolueren.",
      href: "/evolution",
      label: "Bekijk vooruitgang",
    };
  }, [dashboardStats, preintake]);

  const profileRows = [
    { label: "E-mailadres", value: userdetails?.email || "-" },
    { label: "Telefoon", value: userdetails?.phone_number || "-" },
    { label: "Hoofdklacht", value: userdetails?.klacht || "-" },
    { label: "Beroep", value: userdetails?.beroep || "-" },
    {
      label: "Woonplaats",
      value: [userdetails?.zipcode, userdetails?.city].filter(Boolean).join(" ") || "-",
    },
    {
      label: "Lid sinds",
      value: userdetails?.created_at ? moment(userdetails.created_at).format("LL") : "-",
    },
  ];

  const dashboardTabs = [
    { id: "overzicht", label: "Repeat overzicht" },
    { id: "traject", label: "Repeat traject" },
    { id: "profiel", label: "Repeat profiel" },
  ];

  const cardClass =
    "rounded-2xl border border-wa-border bg-wa-panel shadow-sm";
  const quietButtonClass =
    "inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-wa-border bg-wa-panel2 px-3 py-2 text-sm font-semibold text-wa-text transition hover:border-wa-accent/40 hover:bg-wa-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-wa-accent/40";
  const primaryButtonClass =
    "inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-wa-accent px-4 py-2 text-sm font-bold text-wa-onaccent shadow-sm transition hover:brightness-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-wa-accent/40";

  const insightCards = [
    {
      label: "Berichten",
      value: unreadMailCount,
      text: `${unreadMailCount} ongelezen berichten`,
      icon: InboxIcon,
      href: "/mailbox",
    },
    {
      label: "Support",
      value: activeSupportTickets.length,
      text: `${activeSupportTickets.length} open supporttickets`,
      icon: LifebuoyIcon,
      href: "/support",
    },
    {
      label: "Laatste document",
      value: latestDocument?.title || latestDocument?.bestand || "Geen document",
      text: latestDocument?.datum ? moment(latestDocument.datum).format("DD/MM/YYYY") : `${attachments.length} documenten`,
      icon: DocumentTextIcon,
      href: "/documents",
    },
    {
      label: "Sterkste meting",
      value: progressInsights.strongest?.score || 0,
      text: progressInsights.strongest?.label || "Nog geen metingen",
      icon: ChartBarIcon,
      href: "/evolution",
    },
  ];

  const navigateTo = (href) => {
    window.location.assign(href);
  };

  return (
    <div className="min-h-0 h-max bg-wa-app px-4 py-5 text-wa-text sm:px-6 lg:px-8 lg:py-7">
      <div className="mx-auto flex max-w-7xl flex-col gap-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-semibold text-wa-accent">Repeat Connect</p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-wa-text sm:text-4xl">
              Goedemorgen, {getFirstName(userdetails)}.
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-wa-muted">
              Uw zorgoverzicht toont wat vandaag aandacht vraagt, wat klaar is en
              waar uw traject naartoe beweegt.
            </p>
          </div>

          <div
            role="tablist"
            aria-label="Repeat dashboard tabs"
            className="inline-flex w-full gap-1 rounded-2xl border border-wa-border bg-wa-panel p-1 shadow-sm sm:w-auto"
          >
            {dashboardTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`min-h-10 flex-1 rounded-xl px-3 py-2 text-sm font-semibold transition sm:flex-none ${
                  activeTab === tab.id
                    ? "bg-wa-text text-wa-panel shadow-sm"
                    : "text-wa-muted hover:bg-wa-hover hover:text-wa-text"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {activeTab === "overzicht" && (
          <>
            <section className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.3fr)_minmax(320px,0.7fr)]">
              <article className={`${cardClass} overflow-hidden`}>
                <div className="grid gap-6 p-5 sm:p-6 lg:grid-cols-[minmax(0,1fr)_13rem] lg:p-7">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-wa-border bg-wa-panel2 px-3 py-1 text-xs font-semibold text-wa-muted">
                      <CheckCircleIcon className="h-4 w-4 text-wa-accent" aria-hidden="true" />
                      {dashboardStats.journeyPercent}% van uw Repeat traject
                    </div>
                    <h2 className="mt-4 text-2xl font-bold tracking-tight text-wa-text sm:text-3xl">
                      Vandaag in uw zorgreis.
                    </h2>
                    <p className="mt-3 max-w-2xl text-sm leading-6 text-wa-muted">
                      Een compact overzicht van afspraken, berichten, documenten en
                      gezondheidsmetingen die vandaag belangrijk zijn.
                    </p>
                    <div className="mt-5 grid gap-3 sm:grid-cols-2">
                      <div className="rounded-xl border border-wa-border bg-wa-panel2 px-4 py-3">
                        <p className="flex items-center gap-2 text-xs font-semibold text-wa-muted">
                          <CalendarDaysIcon className="h-4 w-4 text-wa-accent" aria-hidden="true" />
                          Volgende afspraak
                        </p>
                        <p className="mt-2 text-base font-bold text-wa-text">
                          {nextAppointment?.title || "Nog geen afspraak gepland"}
                        </p>
                        <p className="mt-1 text-xs leading-5 text-wa-muted">
                          {nextAppointment?.date
                            ? moment(nextAppointment.date).format("DD/MM/YYYY HH:mm")
                            : "Plan een moment met uw specialist"}
                        </p>
                      </div>
                      <div className="rounded-xl border border-wa-border bg-wa-panel2 px-4 py-3">
                        <p className="flex items-center gap-2 text-xs font-semibold text-wa-muted">
                          <MapPinIcon className="h-4 w-4 text-wa-accent" aria-hidden="true" />
                          Locatie en status
                        </p>
                        <p className="mt-2 text-base font-bold text-wa-text">
                          {nextAppointment?.location || "Repeat Connect"}
                        </p>
                        <p className="mt-1 text-xs leading-5 text-wa-muted">
                          {nextAppointment?.accepted ? "Bevestigd door admin" : "Bevestiging volgt"}
                        </p>
                      </div>
                    </div>
                    <div className="mt-5 flex flex-wrap gap-3">
                      <button type="button" onClick={() => navigateTo(nextAction.href)} className={primaryButtonClass}>
                        {nextAction.label}
                        <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <button type="button" onClick={() => navigateTo("/mailbox")} className={quietButtonClass}>
                        <EnvelopeIcon className="h-4 w-4" aria-hidden="true" />
                        Open berichten
                      </button>
                    </div>
                  </div>
                  <div className="relative mx-auto flex aspect-square w-48 items-center justify-center rounded-[2rem] p-4 lg:w-full">
                    <div className="absolute inset-0 rounded-[2rem] bg-[conic-gradient(from_210deg,rgb(var(--wa-accent))_0_68%,rgb(var(--wa-border))_68%_100%)]" />
                    <div className="relative flex h-full w-full flex-col items-center justify-center rounded-[1.45rem] bg-wa-panel text-center">
                      <span className="text-4xl font-black tracking-tight text-wa-text">
                        {dashboardStats.journeyPercent}%
                      </span>
                      <span className="mt-1 text-xs font-semibold text-wa-muted">
                        Zorgreis
                      </span>
                    </div>
                  </div>
                </div>
              </article>

              <article className={`${cardClass} bg-gradient-to-br from-wa-panel via-wa-panel to-wa-panel2 p-5 sm:p-6`}>
                <p className="text-sm font-semibold text-wa-accent">Volgende stap</p>
                <h2 className="mt-2 text-2xl font-bold tracking-tight text-wa-text">
                  {nextAction.title}
                </h2>
                <p className="mt-3 text-sm leading-6 text-wa-muted">{nextAction.text}</p>
                <button
                  type="button"
                  onClick={() => navigateTo(nextAction.href)}
                  className={`${primaryButtonClass} mt-6 w-full`}
                >
                  {nextAction.label}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </article>
            </section>

            <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {insightCards.map((item) => (
                <article key={item.label} className={`${cardClass} p-4 transition hover:border-wa-accent/40 sm:p-5`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-wa-muted">{item.label}</p>
                      <p className="mt-2 truncate text-2xl font-black tracking-tight text-wa-text">
                        {item.value}
                      </p>
                      <p className="mt-1 text-xs leading-5 text-wa-muted">{item.text}</p>
                    </div>
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-wa-panel2 text-wa-accent">
                      <item.icon className="h-5 w-5" aria-hidden="true" />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigateTo(item.href)}
                    className="mt-4 text-xs font-bold text-wa-accent hover:underline"
                  >
                    Bekijken
                  </button>
                </article>
              ))}
            </section>

            <section className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
              <div className="min-w-0">
                <VooruitgangChart
                  selectedMetric={selectedMetric}
                  setSelectedMetric={setSelectedMetric}
                  averageScore={averageScore}
                  metricData={metricData}
                />
              </div>
              <aside className="grid gap-5">
                <section className={`${cardClass} p-5`}>
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <h2 className="text-lg font-bold tracking-tight text-wa-text">
                      Zorgsignalen
                    </h2>
                    <ShieldCheckIcon className="h-5 w-5 text-wa-accent" aria-hidden="true" />
                  </div>
                  <div className="grid gap-3">
                    <div className="rounded-xl border border-wa-border bg-wa-panel2 px-3 py-3">
                      <p className="text-xs font-semibold text-wa-muted">Sterkste meting</p>
                      <p className="mt-1 text-sm font-bold text-wa-text">
                        {progressInsights.strongest?.label || "Nog geen meting"}
                        {progressInsights.strongest ? ` ${progressInsights.strongest.score}` : ""}
                      </p>
                    </div>
                    <div className="rounded-xl border border-wa-border bg-wa-panel2 px-3 py-3">
                      <p className="flex items-center gap-2 text-xs font-semibold text-wa-muted">
                        <ExclamationTriangleIcon className="h-4 w-4 text-wa-accent" aria-hidden="true" />
                        Aandachtspunt
                      </p>
                      <p className="mt-1 text-sm font-bold text-wa-text">
                        {progressInsights.attention?.label || "Geen aandachtspunt"}
                        {progressInsights.attention ? ` ${progressInsights.attention.score}` : ""}
                      </p>
                    </div>
                    <div className="rounded-xl border border-wa-border bg-wa-panel2 px-3 py-3">
                      <p className="text-xs font-semibold text-wa-muted">Laatste update</p>
                      <p className="mt-1 text-sm font-bold text-wa-text">
                        {progressInsights.latestUpdate
                          ? moment(progressInsights.latestUpdate).format("DD/MM/YYYY")
                          : "Nog geen update"}
                      </p>
                    </div>
                  </div>
                </section>

                <section className={`${cardClass} p-5`}>
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <h2 className="text-lg font-bold tracking-tight text-wa-text">
                      Formulieren status
                    </h2>
                    <ClipboardDocumentCheckIcon className="h-5 w-5 text-wa-accent" aria-hidden="true" />
                  </div>
                  <div className="grid gap-2">
                    {sentForms.map((form) => (
                      <div key={form.label} className="flex items-center justify-between gap-3 rounded-xl border border-wa-border bg-wa-panel2 px-3 py-2">
                        <div>
                          <p className="text-sm font-semibold text-wa-text">{form.label}</p>
                          <p className="text-xs text-wa-muted">
                            {form.date ? moment(form.date).format("DD/MM/YYYY") : "Nog niet verzonden"}
                          </p>
                        </div>
                        <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                          form.sent ? "bg-wa-accent/20 text-wa-accent" : "bg-wa-hover text-wa-muted"
                        }`}>
                          {form.sent ? "Verzonden" : "Open"}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>

                <section className={`${cardClass} p-5`}>
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <h2 className="text-lg font-bold tracking-tight text-wa-text">
                      Gebruikersprofiel
                    </h2>
                    <IdentificationIcon className="h-5 w-5 text-wa-accent" aria-hidden="true" />
                  </div>
                  <dl className="grid gap-3">
                    {profileRows.slice(0, 3).map((row) => (
                      <div key={row.label} className="rounded-xl border border-wa-border bg-wa-panel2 px-3 py-2">
                        <dt className="text-xs font-semibold text-wa-muted">{row.label}</dt>
                        <dd className="mt-1 break-words text-sm font-semibold text-wa-text">
                          {row.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>
              </aside>
            </section>
          </>
        )}

        {activeTab === "traject" && (
          <section className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
            <div className="min-w-0">
              {preintake &&
              klachten?.lijst &&
              Object.keys(klachten?.lijst).length > 0 ? (
                <RPProgressbarVragenlijst
                  preintake={preintake}
                  klachtenregistratie={klachten}
                  voedingsdagboek={voedingsdagboek}
                />
              ) : (
                <div className={`${cardClass} flex min-h-[14rem] items-center justify-center px-4 py-8 text-center text-sm text-wa-muted`}>
                  Geen vragenlijsten gevonden.
                </div>
              )}
            </div>
            <aside className={`${cardClass} p-5`}>
              <h2 className="text-lg font-bold tracking-tight text-wa-text">Repeat traject</h2>
              <div className="mt-4 space-y-3">
                {[
                  ["Pre-intake", formatCount(dashboardStats.preintakeDone, dashboardStats.preintakeTotal)],
                  ["Klachtenregistratie", formatCount(dashboardStats.klachtDone, dashboardStats.klachtTotal)],
                  ["Voedingsdagboek", formatCount(dashboardStats.voedingDone, 3)],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between gap-3 rounded-xl border border-wa-border bg-wa-panel2 px-3 py-3">
                    <span className="text-sm font-semibold text-wa-text">{label}</span>
                    <span className="text-sm font-bold text-wa-accent">{value}</span>
                  </div>
                ))}
              </div>
            </aside>
          </section>
        )}

        {activeTab === "profiel" && (
          <section className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
            <article className={`${cardClass} p-5 sm:p-6`}>
              <div className="mb-5 flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-wa-accent">Repeat profiel</p>
                  <h2 className="mt-1 text-2xl font-bold tracking-tight text-wa-text">
                    Gebruikersprofiel
                  </h2>
                </div>
                <IdentificationIcon className="h-7 w-7 text-wa-accent" aria-hidden="true" />
              </div>
              <dl className="grid gap-3 sm:grid-cols-2">
                {profileRows.map((row) => (
                  <div key={row.label} className="rounded-xl border border-wa-border bg-wa-panel2 px-3 py-3">
                    <dt className="text-xs font-semibold text-wa-muted">{row.label}</dt>
                    <dd className="mt-1 break-words text-sm font-semibold text-wa-text">
                      {row.value}
                    </dd>
                  </div>
                ))}
                <div className="rounded-xl border border-wa-border bg-wa-panel2 px-3 py-3 sm:col-span-2">
                  <dt className="text-xs font-semibold text-wa-muted">Situatie</dt>
                  <dd className="mt-1 whitespace-pre-wrap text-sm leading-6 text-wa-text">
                    {userdetails?.more_details || "-"}
                  </dd>
                </div>
              </dl>
            </article>

            <div className="min-w-0">
              {attachments?.length > 0 ? (
                <Attachments attachments={attachments} />
              ) : (
                <div className={`${cardClass} flex min-h-[14rem] items-center justify-center px-4 py-8 text-center text-sm text-wa-muted`}>
                  Geen documenten gevonden.
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default DashboardClient;
